import { useCallback } from "react";

import { useModals } from "hooks";
import { useDealActions } from "store";
import { ReportContext } from "store/types";
import { getValueByPath } from "utils/get-value-by-path";

import { BankPercentageResult } from "./BankPercentageDialog";

export type ClientProcedureHook = (context: ReportContext) => Promise<Record<string, unknown>>;

/**
 * Hook that returns a client procedure for handling investors with multiple bank accounts.
 * If the investor has multiple bank_details (or a single account_number containing ";"),
 * opens the bank percentage dialog and returns { bank_percentages }.
 * Otherwise resolves with {}.
 */
export function usePrePostReportHook(): ClientProcedureHook {
  const { bankPercentageModal } = useModals();
  const { getBankSplitAction } = useDealActions();

  return useCallback(
    async (context: ReportContext) => {
      const investor = context.investor as ReportContext["investor"];
      const bankDetails = investor?.bank_details ?? [];
      const firstAccountNumber = getValueByPath(context, "investor.bank_details.0.account_number", null) as
        | string
        | null
        | undefined;
      const firstContainsSemicolon = typeof firstAccountNumber === "string" && firstAccountNumber.includes(";");

      const hasMultipleBankDetails = bankDetails.length >= 2;
      const hasMultipleAccountsInOne = bankDetails.length && firstContainsSemicolon;
      const shouldShowDialog = hasMultipleBankDetails || hasMultipleAccountsInOne;

      if (!shouldShowDialog) {
        return {};
      }

      let banks: Array<{ bank_detail_id: number; label: string }>;
      let accountNumbersByIndex: string[] | null = null;

      if (hasMultipleAccountsInOne && typeof firstAccountNumber === "string") {
        const labels = firstAccountNumber
          .split(";")
          .map((s) => s.trim())
          .filter(Boolean);
        accountNumbersByIndex = labels;
        banks = labels.map((label, index) => ({
          bank_detail_id: index,
          label: label || `Account ${index + 1}`
        }));
      } else {
        banks = bankDetails.map((b) => ({
          bank_detail_id: b.id,
          label: [b.account_name, b.account_number, b.bank_name].filter(Boolean).join(" – ") || `Account ${b.id}`
        }));
      }

      // Load saved split for this investor/deal to pre-populate the dialog.
      let savedPercentages: Record<number, string> | undefined;
      const investorId = context.investor_id;
      const dealId = context.deal_id;
      if (investorId && dealId) {
        const saved = await getBankSplitAction(investorId, dealId);
        if (saved && saved.length) {
          savedPercentages = {};
          if (accountNumbersByIndex !== null) {
            // Semi-colon split case: match by account_number
            saved.forEach((s) => {
              const idx = accountNumbersByIndex!.indexOf(s.account_number ?? "");
              if (idx >= 0 && savedPercentages) {
                savedPercentages[idx] = String(s.percentage);
              }
            });
          } else {
            // Multiple bank_details case: match by bank_detail_id
            saved.forEach((s) => {
              if (s.bank_detail_id != null && savedPercentages) {
                savedPercentages[s.bank_detail_id] = String(s.percentage);
              }
            });
          }
        }
      }

      const result = await new Promise<BankPercentageResult | null>((resolve) => {
        bankPercentageModal({
          banks,
          savedPercentages,
          action: (r) => resolve(r),
          onCancel: () => resolve(null)
        });
      });

      if (result === null) {
        throw new Error("cancelled");
      }

      const bank_percentages =
        accountNumbersByIndex !== null
          ? result.bank_percentages.map(({ bank_detail_id, percentage }) => {
              const accounts = accountNumbersByIndex!;
              return {
                account_number: accounts[bank_detail_id] ?? `Account ${bank_detail_id + 1}`,
                percentage
              };
            })
          : result.bank_percentages;

      return { bank_percentages };
    },
    [bankPercentageModal, getBankSplitAction]
  );
}
